import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Trash2, CalendarDays } from "lucide-react";
import Modal from "components/ui/Modal";

function safeToDate(v) {
  if (!v) return new Date("Invalid");
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v))
    return new Date(v + "T00:00:00");
  return new Date(v);
}
function pretty(v) {
  const d = safeToDate(v);
  return isNaN(d)
    ? String(v ?? "")
    : d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
}

export default function HolidayTable({ items = [], onDelete }) {
  const nav = useNavigate();

  // 🧠 confirmation modal state
  const [confirm, setConfirm] = useState({
    open: false,
    id: null,
    name: "",
  });
  const [deleting, setDeleting] = useState(false);

  const openConfirm = (id, name) =>
    setConfirm({ open: true, id, name: name || "" });
  const closeConfirm = () => setConfirm({ open: false, id: null, name: "" });

  const handleConfirmDelete = async () => {
    if (!onDelete || !confirm.id) return;
    try {
      setDeleting(true);
      await onDelete(confirm.id); // parent does the API call + reload
      closeConfirm();
    } finally {
      setDeleting(false);
    }
  };

  if (!items.length)
    return (
      <div className="flex flex-col items-center justify-center px-6 py-10 text-center">
        <CalendarDays
          className="h-10 w-10 text-gray-300 dark:text-white/20"
          strokeWidth={1.3}
        />
        <h3 className="mt-3 text-sm font-semibold text-gray-700 dark:text-white">
          No holidays available
        </h3>
        <p className="mt-1 text-xs text-gray-500 dark:text-white/70">
          New holidays will appear here once added.
        </p>
      </div>
    );

  return (
    <>
      <ul className="divide-y divide-gray-100 text-sm dark:divide-white/10">
        {items.map((h) => {
          const id = h.id || h._id; // handle both shapes
          return (
            <li
              key={id || h.date}
              className="flex items-center justify-between py-2"
            >
              <div>
                <div className="font-medium text-navy-700 dark:text-white">
                  {h.name}
                </div>
                <div className="text-xs text-gray-500">
                  {pretty(h.date)} • {h.type}
                  {h.region ? ` • ${h.region}` : ""}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  className="rounded-md  px-2 py-1 text-xs text-blue-500 transition hover:text-blue-700 dark:border-white/10"
                  onClick={() => id && nav(`${id}/edit`)} // use relative nav if nested
                >
                  <Pencil className="h-5 w-5" />
                </button>

                {onDelete && (
                  <button
                    className="text-rose-600 rounded-md px-2 py-1 text-xs text-red-500 transition hover:text-blue-700 dark:hover:bg-white/10"
                    onClick={() => id && openConfirm(id, h.name)}
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {/* 🪟 Confirmation Modal */}
      <Modal
        open={confirm.open}
        onClose={deleting ? undefined : closeConfirm}
        title="Delete holiday?"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-white/80">
            Are you sure you want to delete{" "}
            <span className="font-medium">
              {confirm.name || "this holiday"}
            </span>
            ? This action cannot be undone.
          </p>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              disabled={deleting}
              onClick={closeConfirm}
              className="hover:bg-black rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-60 dark:border-white/10 dark:text-white"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              disabled={deleting}
              className="focus:ring-rose-500  rounded-lg bg-gradient-to-r from-yellow-500 to-red-500 px-5 py-2 text-sm text-white hover:opacity-95 focus:outline-none focus:ring-2 disabled:opacity-60"
            >
              {deleting ? "Deleting…" : "Delete"}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
